import { useRef } from "react";
import {
  gql,
  useClient,
  useMutation,
  useQuery as useUrqlQuery,
  useSubscription,
} from "urql";

export { gql, useClient, useMutation, useSubscription };

/**
 * urql 2.x conserva a propósito el resultado anterior cuando cambian las variables.
 * En `computeNextState`:
 *
 *   data: result.data !== undefined || result.error ? result.data : prevState.data
 *
 * Para paginación o filtros eso es deseable: la tabla no parpadea mientras carga la
 * página siguiente. Para el cambio de empresa no lo es: la pantalla sigue mostrando
 * los datos de la empresa anterior durante todo el round trip, bajo el encabezado de
 * la empresa nueva.
 *
 * Este wrapper descarta el `data` que pertenece a otra empresa y deja intacto el resto
 * del comportamiento, incluida la pintada inmediata desde caché cuando la empresa nueva
 * ya se había consultado.
 */
const SCOPE_KEYS = ["companyId", "companyIds"];

const formatScopeValue = (value) =>
  Array.isArray(value) ? [...value].map(String).sort().join(",") : String(value);

// Varias queries mandan la empresa anidada (`variables: { input: { companyId } }`,
// ver GetDocuments), así que no alcanza con mirar el primer nivel.
const collectScope = (value, parts, depth) => {
  if (!value || typeof value !== "object" || depth > 3) return;
  if (Array.isArray(value)) {
    for (const item of value) collectScope(item, parts, depth + 1);
    return;
  }
  for (const [key, nested] of Object.entries(value)) {
    if (SCOPE_KEYS.includes(key)) {
      if (nested !== undefined && nested !== null) {
        parts.push(`${key}:${formatScopeValue(nested)}`);
      }
      continue;
    }
    collectScope(nested, parts, depth + 1);
  }
};

const scopeOf = (variables) => {
  const parts = [];
  collectScope(variables, parts, 0);
  return parts.sort().join("|");
};

export const useQuery = (args) => {
  const [result, reexecuteQuery] = useUrqlQuery(args);
  const scope = scopeOf(args?.variables);

  // Se recuerda a qué empresa pertenece el `data` vigente. urql entrega un objeto
  // nuevo cuando llega el resultado de la clave nueva; mientras arrastra el anterior
  // es exactamente el mismo objeto, así que la comparación por identidad alcanza.
  const owner = useRef({ scope, data: result.data });
  if (result.data !== owner.current.data) {
    owner.current = { scope, data: result.data };
  }

  if (owner.current.scope === scope) return [result, reexecuteQuery];
  return [{ ...result, data: undefined }, reexecuteQuery];
};
